import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import moreImageSm from "../assets/images/trading/navigation.svg";
import sellImageSm from "../assets/images/trading/sell.svg";
import eightPayImageSm from "../assets/images/dashboard/88pay.png";
import buyCryptoImageSm from "../assets/images/dashboard/buy-sm.png";
import marketIcon from "../assets/images/trading/market.svg";
import buySellIcon from "../assets/images/trading/buySell.svg";
import sendIcon from "../assets/images/trading/sendReceive.svg";
import withdrawDepositIcon from "../assets/images/trading/widtdrawDeposit.svg";
import eightyEightPayIcon from "../assets/images/trading/88Pay.svg";
import { useAuth } from "../context/AuthContext";
import {
  useActiveAccount,
  useFeatureFlagsByCategory,
} from "../hooks/query/queries";

export function TradingQuickActions() {
  const { user: userProfile } = useAuth();
  const userId = userProfile?._id;
  const accountId = userProfile?.subAccountDetails[0]?._id;
  const { data: activeAccount } = useActiveAccount(userId, accountId);
  const mainAccount = activeAccount?.isMainAccount;
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);

  const quickActions = [
    featureFlags?.buyCrypto && {
      title: "Buy",
      smImage: buyCryptoImageSm,
      smText: "Buy",
    },
    featureFlags?.sellCrypto && {
      title: "Sell",
      smImage: sellImageSm,
      smText: "Sell",
    },
    featureFlags?.eightPay && {
      title: "88Pay",
      smImage: eightPayImageSm,
      smText: "88Pay",
    },
    {
      title: "More",
      smImage: moreImageSm,
      smText: "More",
      onMoreClick: () => setIsMoreModalOpen(true),
    },
  ].filter(Boolean);

  return (
    <>
      <div className="grid grid-cols-4 gap-3 justify-items-center">
        {quickActions.map((action) => (
          <ActionButton key={action.title} {...action} />
        ))}
      </div>
      <MoreActionsModal
        isOpen={isMoreModalOpen}
        onClose={() => setIsMoreModalOpen(false)}
        mainAccount={mainAccount}
        featureFlags={featureFlags}
      />
    </>
  );
}

const ActionButton = ({ title, smImage, smText, onMoreClick }) => {
  const navigate = useNavigate();

  const handleClick = (title) => {
    if (title === "More" && onMoreClick) {
      onMoreClick();
      return;
    }

    let selectedTab = null;
    let parentTab = null;
    let route = "/";

    switch (title) {
      case "Buy":
        selectedTab = "Buy";
        parentTab = "buy-sell";
        route = "/trading/digitalWallet";
        break;
      case "Sell":
        selectedTab = "Sell";
        parentTab = "buy-sell";
        route = "/trading/digitalWallet";
        break;
      case "88Pay":
        selectedTab = "Pay";
        parentTab = "80eightpay";
        route = "/trading/digitalWallet";
        break;
      default:
        route = "/";
    }

    navigate(route, {
      state: {
        selectedTab,
        parentTab,
      },
    });
  };

  return (
    <Button
      onClick={() => handleClick(title)}
      variant="outline"
      className="relative border-0 bg-transparent text-white hover:text-white/80 flex flex-col items-center justify-center gap-2 h-auto py-3 px-3 overflow-hidden w-full max-w-[80px]"
    >
      <div className="flex items-center justify-center w-[64px] h-[64px] rounded-full bg-white/10 border-[1px] border-[#ffffff54]">
        <img
          src={smImage}
          alt={title}
          className="h-6 w-6 relative z-10 object-contain"
        />
      </div>
      <div className="text-[12px] font-normal text-white text-center relative z-10 leading-tight">
        {smText}
      </div>
    </Button>
  );
};

const SvgIcon = ({ src }) => {
  return (
    <img
      src={src}
      alt=""
      className="w-[24px] h-[24px] flex items-center justify-center"
      style={{
        filter: "brightness(0) invert(1)",
        display: "block",
      }}
    />
  );
};

const MoreActionsModal = ({ isOpen, onClose, mainAccount, featureFlags }) => {
  const navigate = useNavigate();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const menuItems = [
    mainAccount && {
      id: "market",
      label: "Market",
      description: "Explore the market",
      icon: marketIcon,
      parentTab: "market",
    },
    (featureFlags?.buyCrypto || featureFlags?.sellCrypto) && {
      id: "buy-sell",
      label: "Buy / Sell",
      description: "Buy or sell crypto",
      icon: buySellIcon,
      parentTab: "buy-sell",
    },
    mainAccount &&
      (featureFlags?.sendCrypto || featureFlags?.receiveCrypto) && {
        id: "send-receive",
        label: "Send / Receive",
        description: "Send or receive crypto",
        icon: sendIcon,
        parentTab: "send-receive",
      },
    mainAccount &&
      (featureFlags?.deposit || featureFlags?.withdraw) && {
        id: "withdraw-deposit",
        label: "Withdraw / Deposit",
        description: "Withdraw or deposit fiat",
        icon: withdrawDepositIcon,
        parentTab: "withdraw-deposit",
      },
    mainAccount &&
      featureFlags?.eightPay && {
        id: "80eightpay",
        label: "88Pay",
        description: "Send or receive money",
        icon: eightyEightPayIcon,
        parentTab: "80eightpay",
      },
  ].filter(Boolean);

  const handleItemClick = (item) => {
    navigate("/trading/digitalWallet", {
      state: {
        parentTab: item.parentTab,
      },
    });
    onClose();
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isDownSwipe = distance < -minSwipeDistance;
    if (isDownSwipe) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="fixed left-0 right-0 bottom-0 top-auto !translate-x-0 !translate-y-0 max-w-full w-full p-0 bg-white rounded-t-2xl border-0 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-300 !zoom-in-100 !zoom-out-100">
        <div
          className="relative p-6 pb-8"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="space-y-2 mt-2">
            <div className="h-[5px] w-[32px] mx-auto rounded-full bg-[#F1EDF7] relative z-10 top-[-20px]"></div>
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                className="w-full flex items-center gap-4 px-4 h-[72px] rounded-2xl hover:bg-gray-50 transition-colors text-left border border-[1px] border-[#F1EDF7]"
              >
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-full"
                  style={{
                    background:
                      "linear-gradient(86.72deg, #57249C -10.66%, #EB3366 113.47%)",
                  }}
                >
                  <SvgIcon src={item.icon} />
                </div>
                <div className="flex-1">
                  <div className="text-[16px] font-semibold text-[#33125F]">
                    {item.label}
                  </div>
                  <div className="text-[12px] text-[#6D6584]">
                    {item.description}
                  </div>
                </div>
                <div className="border relative z-10 rounded-full h-[40px] w-[40px] flex items-center justify-center border-[#F1EDF7]">
                  <ArrowRight className="text-[#6D6584] w-5 h-5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
